'use client';

import {
  Alert,
  AuthCard,
  AuthCardContent,
  AuthCardHeader,
  AuthCardMessage,
  AuthCardSecondaryAction,
} from '@lulalife/design-system';
import { type ComponentProps, type ReactNode, useId } from 'react';

export type AuthScreenProps = {
  title: string;
  /** Sentence under the title (the MFA prompt, the set-password reason, the email-sent line). */
  description?: ReactNode;
  /** The form-level message in the card's alert slot. Errors that belong to one field stay on that field. */
  alert?: { type: 'success' | 'error'; message: ReactNode } | null;
  /** Given, the card is the `<form>` (Enter submits, password managers recognise it); omitted, it is a plain section. */
  onSubmit?: ComponentProps<typeof AuthCard>['onSubmit'];
  autoComplete?: string;
  /** Lands on the card (the form, when there is one). */
  'data-testid'?: string;
  /** The escape link under the divider (Back to Login, Resend code). */
  secondaryAction?: ReactNode;
  /** The form body: inputs, requirements, buttons. */
  children?: ReactNode;
};

/**
 * One signed-out card: logo, title, the form-level alert, the form body, and the escape link
 * under a divider. Every sign-in screen is this with different children, so the card's order,
 * its accessible name and its alert wiring are written once here.
 *
 * It sits inside `AuthAppShell`, which `AuthLayout` renders around the whole `(auth)` group.
 */
export const AuthScreen = ({
  title,
  description,
  alert,
  onSubmit,
  autoComplete,
  'data-testid': dataTestId = 'auth-card',
  secondaryAction,
  children,
}: AuthScreenProps) => {
  const titleId = useId();

  return (
    <AuthCard
      as={onSubmit ? 'form' : 'section'}
      aria-labelledby={titleId}
      onSubmit={onSubmit}
      autoComplete={autoComplete}
      data-testid={dataTestId}
    >
      <AuthCardHeader
        brand="foresight"
        title={title}
        description={description}
        titleProps={{ id: titleId }}
      />
      <AuthCardMessage data-testid={alert ? `auth.${alert.type}-alert` : undefined}>
        {alert && (
          <Alert type={alert.type} role={alert.type === 'error' ? 'alert' : 'status'}>
            {alert.message}
          </Alert>
        )}
      </AuthCardMessage>
      {children ? <AuthCardContent>{children}</AuthCardContent> : null}
      {secondaryAction ? (
        <AuthCardSecondaryAction>{secondaryAction}</AuthCardSecondaryAction>
      ) : null}
    </AuthCard>
  );
};

export default AuthScreen;
